import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import type {
  DocumentTagSearchResponse,
  DocumentTagSearchResult,
} from "@/features/document-intelligence/search-document-tags-dialog";
import type { TagDetectionLayerStyle } from "@/features/document-intelligence/tag-detection-layer";
import { hexToRgba } from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";

export interface FieldPilotPlanTagSearchMatch {
  canonicalTagId: Id<"documentIntelligenceCanonicalTags">;
  fieldPilotTagId: Id<"fieldPilotTags"> | null;
  displayTag: string;
  normalizedTag: string;
  detectionCount: number;
  documentCount: number;
  pageCount: number;
  incompleteDocumentCount: number;
}

export interface FieldPilotPlanTagSearchResponse {
  status: "too_short" | "no_match" | "matched";
  matches: FieldPilotPlanTagSearchMatch[];
}

export interface FieldPilotTagLocation {
  detectionId: Id<"documentIntelligenceDetections">;
  fieldPilotTagId: Id<"fieldPilotTags">;
  planDocumentId: Id<"fieldPilotPlanDocuments">;
  planDocumentName: string;
  documentOrder: number;
  pageNumber: number;
  bbox: { x: number; y: number; width: number; height: number };
  alreadyConverted: boolean;
}

/**
 * Typed alias for FieldPilot detection APIs registered in Convex `_generated`.
 */
export const fieldPilotDetectionsApi = api.fieldPilot.detections;

export const FIELD_PILOT_TAG_SEARCH_EMPTY_MESSAGES = {
  beforeSearch: "Enter at least 2 characters to search.",
  tooShort: "Search for at least 2 characters.",
  availableInJob: (query: string) =>
    `${query} is already available in the tag list. Use the Tags dropdown to add it to results.`,
  noMatch:
    "No indexed matches in this FieldPilot plan set. Check the tag text or confirm the drawings have finished indexing.",
} as const;

export function mapFieldPilotSearchToDialogResponse(
  response: FieldPilotPlanTagSearchResponse
): DocumentTagSearchResponse {
  if (response.status === "too_short") {
    return { status: "too_short", matches: [] };
  }
  if (response.status === "no_match" || response.matches.length === 0) {
    return { status: "no_match", matches: [] };
  }

  const matches: DocumentTagSearchResult[] = response.matches.map((match) => ({
    _id: match.canonicalTagId,
    rawValue: match.displayTag,
    normalizedValue: match.normalizedTag,
    detectionCount: match.detectionCount,
    revisionCount: match.documentCount,
    documentCount: match.documentCount,
    pageCount: match.pageCount,
    incompleteDocumentCount: match.incompleteDocumentCount,
    alreadyInJob: match.fieldPilotTagId !== null,
  }));

  return { status: "matched", matches };
}

export function getFieldPilotDetectionStyle(
  colorHex: string
): TagDetectionLayerStyle {
  return {
    color: colorHex,
    fillColor: hexToRgba(colorHex, 0.18),
  };
}

export interface FieldPilotTagLocationSummary {
  tagId: Id<"fieldPilotTags">;
  totalCount: number;
  returnedCount: number;
  isTruncated: boolean;
}

export function summarizeLocationsForTag(
  locations: FieldPilotTagLocation[],
  tagId: Id<"fieldPilotTags">,
  options?: {
    isTruncated?: boolean;
    totalCount?: number;
    byTag?: FieldPilotTagLocationSummary[];
  }
) {
  const forTag = locations.filter((location) => location.fieldPilotTagId === tagId);
  const unconverted = forTag.filter((location) => !location.alreadyConverted);
  const documentIds = new Set(forTag.map((location) => String(location.planDocumentId)));
  const unconvertedDocumentIds = new Set(
    unconverted.map((location) => String(location.planDocumentId))
  );
  const tagSummary = options?.byTag?.find(
    (entry) => String(entry.tagId) === String(tagId)
  );
  const isTruncated = tagSummary?.isTruncated ?? options?.isTruncated ?? false;
  const resultTotalCount = tagSummary?.totalCount ?? options?.totalCount;

  return {
    total: forTag.length,
    unconvertedCount: unconverted.length,
    convertedCount: forTag.length - unconverted.length,
    documentCount: documentIds.size,
    unconvertedDocumentCount: unconvertedDocumentIds.size,
    unconverted,
    isTruncated,
    resultTotalCount,
  };
}

export function formatConvertActionLabel(summary: {
  total: number;
  unconvertedCount: number;
  convertedCount: number;
  isTruncated?: boolean;
}): { label: string; disabled: boolean; muted?: boolean } {
  if (summary.isTruncated && summary.total === 0) {
    return {
      label: "Too many detections to load",
      disabled: true,
      muted: true,
    };
  }
  if (summary.total === 0) {
    return { label: "No indexed tags", disabled: true, muted: true };
  }
  if (summary.unconvertedCount === 0) {
    if (summary.isTruncated) {
      return {
        label: "Loaded points created (more may exist)",
        disabled: true,
      };
    }
    return { label: "All points created", disabled: true };
  }
  if (summary.isTruncated) {
    return {
      label: `Results truncated (${summary.unconvertedCount} loaded)`,
      disabled: true,
    };
  }
  if (summary.convertedCount > 0) {
    return {
      label: `Create remaining points (${summary.unconvertedCount})`,
      disabled: false,
    };
  }
  return {
    label: `Create points (${summary.unconvertedCount})`,
    disabled: false,
  };
}
